/**
 * kanbanUtils.js — Shared utilities for the Kanban video board
 *
 * Status calculation mirrors the backend's calculate_video_status()
 * (server.py lines 508-539). The backend uses string fields:
 *   checklist_postagem: "Publicado" | ""
 *   checklist_pronto:   "Pronto"    | ""
 *   checklist_roteiro:  "Pronto"    | ""
 */

// ── Column definitions (4 columns matching backend statuses) ──

// Static fallback (used by organizeVideosByColumn — only needs ids, not translated text)
export const COLUMNS = [
  { id: 'planejamento', title: 'Planejamento', icon: '\u{1F4CB}', color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.1)', description: '' },
  { id: 'producao', title: 'Produção', icon: '\u{1F3AC}', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)', description: '' },
  { id: 'finalizado', title: 'Finalizado', icon: '\u{2705}', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)', description: '' },
  { id: 'postado', title: 'Publicado', icon: '\u{1F680}', color: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.1)', description: '' },
];

// Translated columns (used by UI components)
export const getColumns = (t) => [
  {
    id: 'planejamento',
    title: t('kanban.col_planning'),
    icon: '\u{1F4CB}',
    color: '#6B7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
    description: t('kanban.col_planning_desc'),
  },
  {
    id: 'producao',
    title: t('kanban.col_production'),
    icon: '\u{1F3AC}',
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    description: t('kanban.col_production_desc'),
  },
  {
    id: 'finalizado',
    title: t('kanban.col_finished'),
    icon: '\u{2705}',
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    description: t('kanban.col_finished_desc'),
  },
  {
    id: 'postado',
    title: t('kanban.col_published'),
    icon: '\u{1F680}',
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    description: t('kanban.col_published_desc'),
  },
];

// ── Status calculation (mirrors backend exactly) ──

export const calculateVideoStatus = (video) => {
  const postagem = (video.checklist_postagem || '').trim();
  const pronto = (video.checklist_pronto || '').trim();
  const roteiro = (video.checklist_roteiro || '').trim();

  if (postagem) return 'postado';
  if (pronto) return 'finalizado';
  if (roteiro) return 'producao';
  return 'planejamento';
};

// ── Organize videos into columns ──

export const organizeVideosByColumn = (videos) => {
  const columns = {
    planejamento: [],
    producao: [],
    finalizado: [],
    postado: [],
  };

  videos.forEach((video) => {
    // Use pre-computed status from backend if available, else calculate
    const status = video.status || calculateVideoStatus(video);
    if (columns[status]) {
      columns[status].push(video);
    } else {
      columns.planejamento.push(video);
    }
  });

  // Sort: planejamento/producao/finalizado by created_at desc, postado by data_postagem desc
  const byCreatedDesc = (a, b) =>
    new Date(b.created_at || 0) - new Date(a.created_at || 0);

  columns.planejamento.sort(byCreatedDesc);
  columns.producao.sort(byCreatedDesc);
  columns.finalizado.sort(byCreatedDesc);
  columns.postado.sort((a, b) => {
    // data_postagem is "DD/MM/YYYY" string — parse it
    const parseDate = (d) => {
      if (!d) return 0;
      const parts = d.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
      }
      return new Date(d).getTime();
    };
    return parseDate(b.data_postagem) - parseDate(a.data_postagem);
  });

  return columns;
};

// ── Calculate checkbox values for target column when dragging ──

export const calculateCheckboxesForColumn = (targetStatus, video) => {
  switch (targetStatus) {
    case 'planejamento':
      return {
        checklist_titulo: '',
        checklist_roteiro: '',
        checklist_audio: '',
        checklist_thumbnail: '',
        checklist_pronto: '',
        checklist_postagem: '',
      };

    case 'producao':
      // Set roteiro, clear pronto and postagem, keep others as-is
      return {
        checklist_roteiro: 'Pronto',
        checklist_pronto: '',
        checklist_postagem: '',
      };

    case 'finalizado':
      // Mark all production checkboxes, clear postagem
      return {
        checklist_titulo: video.checklist_titulo || 'Pronto',
        checklist_roteiro: 'Pronto',
        checklist_audio: video.checklist_audio || 'Pronto',
        checklist_thumbnail: video.checklist_thumbnail || 'Pronto',
        checklist_pronto: 'Pronto',
        checklist_postagem: '',
      };

    case 'postado':
      // Block — cannot drag directly to postado
      return null;

    default:
      return null;
  }
};

// ── Progress calculation (0-100) ──

export const calculateProgress = (video) => {
  const checks = [
    !!(video.checklist_titulo || '').trim(),
    !!(video.checklist_roteiro || '').trim(),
    !!(video.checklist_audio || '').trim(),
    !!(video.checklist_thumbnail || '').trim(),
    !!(video.checklist_pronto || '').trim(),
    !!(video.checklist_postagem || '').trim(),
  ];
  const completed = checks.filter(Boolean).length;
  return Math.round((completed / checks.length) * 100);
};

// ── Progress color ──

export const getProgressColor = (progress) => {
  if (progress < 33) return '#EF4444';
  if (progress < 66) return '#F59E0B';
  if (progress < 100) return '#3B82F6';
  return '#10B981';
};

// ── Format date string (DD/MM/YYYY → DD/MM) ──

export const formatDate = (dateStr) => {
  if (!dateStr) return null;
  // Handle "DD/MM/YYYY" format
  const parts = dateStr.split('/');
  if (parts.length >= 2) {
    return `${parts[0]}/${parts[1]}`;
  }
  // Fallback for ISO dates
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  } catch {
    return dateStr;
  }
};

// ── Check if video is overdue ──

export const isVideoOverdue = (video) => {
  if (!video.data_postagem) return false;
  if ((video.checklist_postagem || '').trim()) return false; // Already posted

  const parts = video.data_postagem.split('/');
  let date;
  if (parts.length === 3) {
    date = new Date(parts[2], parts[1] - 1, parts[0]);
  } else {
    date = new Date(video.data_postagem);
  }
  return date < new Date();
};

// ── Column index for ordering comparisons ──

const STATUS_ORDER = {
  planejamento: 0,
  producao: 1,
  finalizado: 2,
  postado: 3,
};

export const getStatusIndex = (status) => STATUS_ORDER[status] ?? 0;
